import React, {useState} from "react";
import {Params, useParams} from "react-router-dom";
import SpeechBubble from "../../../../../components/speech-bubble/SpeechBubble";
import CharacterData from "../../../../../model/CharacterData";
import ContentBox from "../../../../../components/content-box/ContentBox";
import PageActions from "../../../../base/PageActions";
import useBrowserNavigation from "../../../../../hooks/useBrowserNavigation";
import useFetchResult from "../../../../../hooks/UseFetchResult";
import _naming_service from "../../../../../services/NamingService";
import _http_service from "../../../../../services/HttpService";
import _image_service from "../../../../../services/ImageService";
import _html_parser from "../../../../../services/HtmlService";
import '../../../../styles/Common.scss';
import './CharacterDisplayPage.scss';

interface CharacterDisplayPageProps {
}

interface CharacterDisplayPageParams extends Params {
    name: string
}

const CharacterDisplayPage = (props: CharacterDisplayPageProps) => {
    
    const params = useParams<CharacterDisplayPageParams>();
    const [name, setName] = useState<string>(params.name!);
    
    const page: PageActions = new PageActions({
        title: `Meet ${_naming_service.capitalizeName(name)} - Chase the Fox`,
        description: `Meet ${_naming_service.capitalizeName(name)}, a denizen of the world of Casarok.`,
        keywords: ["furry oc", "character", "casarok", "toon", name],
        imgSrc: `chase/characters/${name}/1.png`,
        url: `characters/${name}`
    });

    const [getResult, hasFetchedResult, setFetchedResult] = useFetchResult<CharacterData>(undefined, () =>
        _http_service.getCharacterData(name)
            .then(response => {
                if (!response || Object.keys(response).length === 0 || response.category.includes('GUILD')) {
                    throw new Error('No response for character data!');
                }
                
                return response;
            }), [name]);

    useBrowserNavigation((path) => {
        setName(path.substring(path.lastIndexOf('characters/') + 'characters/'.length));
        setFetchedResult(false);
    });
    
    return page.generateMetaContent(
        <>
            <ContentBox>
                {(!hasFetchedResult || !getResult()) ? <></> :
                    <>
                        <SpeechBubble text={getResult()?.quote || ''}/>
                        <div className="character-info">
                            <div className="character-image character-image-right">
                                <img
                                    src={_image_service.getCdnImageSrc(`chase/characters/${name}/1.png`)}
                                    alt={_naming_service.capitalizeName(name)}
                                    className="character"/>
                            </div>
                            <p>
                                <strong>Full name: </strong><span>{getResult()?.fullName}</span><br/>
                                {(getResult()?.otherNames)
                                    ? <><strong>Other names: </strong>
                                        <span>{_html_parser.parse(getResult()?.otherNames || '')}</span><br/></>
                                    : <></>}
                                <strong>Species: </strong><span>{_html_parser.parse(getResult()?.species || '')}</span> (<em>{_html_parser.parse(getResult()?.speciesScientificName || '')}</em>)<br/>
                                <strong>Gender: </strong> <span>{getResult()?.gender}</span><br/>
                                {(getResult()?.category === 'PRESENT')
                                    ? <><strong>Age: </strong> <span>{getResult()?.lifetime}</span><br/></>
                                    : <><strong>Lifetime: </strong> <span>{getResult()?.lifetime}</span><br/></>}
                                <strong>Height: </strong><span>{getResult()?.heightUs}</span> (<span>{getResult()?.heightMetric}</span>)<br/>
                                <strong>Occupation: </strong> <span>{_html_parser.parse(getResult()?.occupation || '')}</span><br/>
                                <strong>Favorite Color: </strong> <span>{getResult()?.favoriteColor}</span><br/>
                                <strong>Favorite Food: </strong> <span>{_html_parser.parse(getResult()?.favoriteFood || '')}</span><br/>
                                <strong>Likes: </strong> <span>{_html_parser.parse(getResult()?.likes || '')}</span><br/>
                                <strong>Dislikes: </strong> <span>{_html_parser.parse(getResult()?.dislikes || '')}</span>
                            </p>
                            <p>
                                <strong>Character Traits: </strong>
                                <span>{_html_parser.parse(getResult()?.traits || '')}</span>
                            </p>
                            <p>
                                <strong>Abilities: </strong>
                                <span>{_html_parser.parse(getResult()?.abilities || '')}</span>
                            </p>
                            <p>
                                <strong>Skills: </strong>
                                <span>{_html_parser.parse(getResult()?.skills || '')}</span>
                            </p>
                            <p>
                                <strong>{(getResult()?.category === 'PRESENT') ? 'Background: ' : 'Story: '}</strong>
                                <span>{_html_parser.parse(getResult()?.story1 || '')}</span>
                            </p>
                            <div className="character-image-left">
                                <img
                                    src={_image_service.getCdnImageSrc(`chase/characters/${name}/2.png`)}
                                    alt={_naming_service.capitalizeName(name)}
                                    className="character"/>
                            </div>
                            <div>
                                <span>{_html_parser.parse(getResult()?.story2 || '')}</span>
                            </div>
                        </div>
                    </>}
            </ContentBox>
        </>
    );
}

export default CharacterDisplayPage;